import {
  isWrapperToGameClientEventType,
  WRAPPER_TO_GAME_CLIENT_EVENT_TYPES,
  WrapperToGameClientEventType,
} from '@/features/event-handlers/wrapper-event-handlers/game-client-events/types';
import {
  isWrapperToProviderEvent,
  WRAPPER_TO_PROVIDER_EVENT_TYPES,
  WrapperToProviderEventType,
} from '@/features/event-handlers/wrapper-event-handlers/provider-events/types';
import {
  getLauncherPostMessageTarget,
  resolveLauncherPostMessageOrigin,
} from '@/features/wrapper-proxy/resolve-launcher-message-origin';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';

/** GL/* events emitted by the iOS native bridge into the same window (intentional subset). */
const IOS_SYNTHETIC_WRAPPER_EVENT_TYPES = new Set<string>([
  WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.INITIALIZE_PARAMS,
  WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.INITIALIZE_URL,
  WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.AUTHENTICATION_TOKEN_UPDATE,
  WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.IOS_SOFT_RELOAD,
  WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.GAME_WINDOW_UI_ENABLED,
  WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_PAYTABLE,
  WRAPPER_TO_PROVIDER_EVENT_TYPES.RELOAD_BALANCE,
  WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_HELP,
  WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_AUDIO,
  WRAPPER_TO_PROVIDER_EVENT_TYPES.DISMISS_ERROR,
  WRAPPER_TO_PROVIDER_EVENT_TYPES.AUTO_SPIN_STOP,
  WRAPPER_TO_PROVIDER_EVENT_TYPES.AUTO_SPIN_RESUME,
  WRAPPER_TO_PROVIDER_EVENT_TYPES.FREE_SPINS_DIALOG,
]);

const DEV_ORIGIN_PORTS = /:(5173|3000)$/;

const stripDevOriginPort = (origin: string): string =>
  origin.replace(/:5173$/, '').replace(/:3000$/, '');

/** Match origins exactly; relax dev ports only when the expected origin uses one. */
const originsMatch = (eventOrigin: string, expectedOrigin: string): boolean => {
  if (eventOrigin === expectedOrigin) {
    return true;
  }

  if (!DEV_ORIGIN_PORTS.test(expectedOrigin)) {
    return false;
  }

  return (
    eventOrigin === stripDevOriginPort(expectedOrigin) ||
    stripDevOriginPort(eventOrigin) === stripDevOriginPort(expectedOrigin)
  );
};

const isSameWindowMessage = (event: MessageEvent): boolean =>
  event.source === window;

export const isWrapperLifecycleMessage = (type: unknown): type is string =>
  typeof type === 'string' &&
  (type === WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.REQUEST_DETACH_SESSION_SNAPSHOT ||
    isWrapperToGameClientEventType(type as WrapperToGameClientEventType) ||
    isWrapperToProviderEvent(type as WrapperToProviderEventType));

const isTrustedParentOrigin = (origin: string): boolean => {
  if (!origin || origin === 'null') {
    return false;
  }

  try {
    const expectedOrigin = resolveLauncherPostMessageOrigin();
    if (originsMatch(origin, expectedOrigin)) {
      return true;
    }
  } catch {
    // Fall through to direct parent lookup.
  }

  try {
    if (window.parent !== window) {
      const parentOrigin = window.parent.location.origin;
      if (originsMatch(origin, parentOrigin)) {
        return true;
      }
    }
  } catch {
    // Cross-origin parent — expectedOrigin resolution above must match.
  }

  return false;
};

const isAllowedSameWindowSynthetic = (type: string): boolean => {
  if (type === WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.GEOLOCATION_MESSAGE) {
    return true;
  }

  if (WrapperProxy.isIosWrapper()) {
    return IOS_SYNTHETIC_WRAPPER_EVENT_TYPES.has(type);
  }

  return false;
};

/**
 * Returns true when a wrapper lifecycle `GL/*` message should be handled.
 * Provider iframe traffic and unrelated message types are not validated here.
 */
export const isTrustedWrapperMessage = (event: MessageEvent): boolean => {
  const type = event?.data?.type;

  if (!isWrapperLifecycleMessage(type)) {
    return true;
  }

  if (isSameWindowMessage(event)) {
    return isAllowedSameWindowSynthetic(type);
  }

  const expectedSource = getLauncherPostMessageTarget();
  if (!expectedSource || event.source !== expectedSource) {
    return false;
  }

  return isTrustedParentOrigin(event.origin);
};
